import { Redirect } from "react-router-dom";
import { z } from "zod";
import Loader from "../../components/Loader";
import { SUPPORTED_LANGUAGES } from "../../config/i18n";
import { useApiQuery } from "../../services/api";
import { AuthTokenUnavailableError } from "../../../../api/src/shared/request";
import Error404Page from "../static/Error404Page";

// The URL is a boundary: an email link may carry anything.
const paramsSchema = z.object({
  pairId: z.uuid(),
  language: z.enum(SUPPORTED_LANGUAGES),
});

/**
 * The CTA of an activation email lands here. The API copies the pair into the
 * reader's own conversation at the first read and returns that same copy
 * after, so a reload or a second click opens the same conversation.
 */
export default function ActivationPairPage(props: {
  pairId: string;
  language: string;
}) {
  const params = paramsSchema.safeParse(props);
  if (!params.success) return <Error404Page />;
  return <ActivationPairRedirect {...params.data} />;
}

function ActivationPairRedirect({
  pairId,
  language,
}: z.infer<typeof paramsSchema>) {
  const conversationQuery = useApiQuery(
    "/assistant/activation-pairs/:pairId/:language/conversation",
    // Asked again at each visit: the reader may have deleted the copy.
    { param: { pairId, language }, staleTime: 0 }
  );
  if (conversationQuery.state === "error") {
    if (conversationQuery.status === 404) return <Error404Page />;
    // A signed-out reader of the link: on desktop the login gate sits over
    // this page, which waits under it. The opened session refetches the query
    // (`revalidateApiCache`).
    if (conversationQuery.error instanceof AuthTokenUnavailableError)
      return <Loader expanded />;
    throw new Error(
      `The conversation of activation pair ${pairId} failed with ${String(conversationQuery.status)}`
    );
  }
  // A cached body can name a copy that the reader deleted since, and the API
  // then makes a new copy: only a fresh response redirects.
  if (conversationQuery.state !== "success" || conversationQuery.isFetching)
    return <Loader expanded />;
  return (
    <Redirect
      to={`/gpt/chat/conversation/${conversationQuery.body.conversationId}`}
    />
  );
}
